import { defineComponent, useListeners, computed, ref, reactive, inject, watch, set } from 'vue';
import debounce from 'lodash.debounce';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import UiAssetClassBadge from '../ui-asset-class-badge/UiAssetClassBadge.vue.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import { showNoResults, showResults } from '../../helpers/data.js';
import { getValidationErrors } from '../../helpers/validation.js';
import { generateElementId } from '../../helpers/generators.js';
import { vClickOutside } from '../../directives/click-outside/index.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "SearchSuggester",
  props: {
    locale: { type: null, required: true },
    assetClasses: { type: Array, required: false, default: () => ["stock", "bond", "commodity", "currency", "etf", "fund", "future", "index", "generic", "interestRate", "investmentProduct", "leveragedProduct", "option"] },
    ItemsPerAssetClass: { type: Number, required: false, default: 5 },
    criteria: { type: null, required: false },
    groupByAssetClass: { type: Boolean, required: false, default: false },
    closeOnSelect: { type: Boolean, required: false, default: true }
  },
  emits: ["store-error", "data-load", "on-select-result", "on-search", "on-all-search-results"],
  setup(__props, { expose, emit: emits }) {
    const props = __props;
    const listeners = useListeners();
    const componentInputs = computed(() => ({
      language: props.locale,
      criteria: props.criteria,
      searchTerm: searchTerm.value,
      numberOfItemsPerAssetClass: props.ItemsPerAssetClass,
      assetClasses: props.assetClasses
    }));
    const searchTerm = ref("");
    const selectedGroupIndex = ref(null);
    const selectedItemIndex = ref(null);
    const elementId = generateElementId();
    const isFocused = ref(false);
    const isClickedIn = ref(false);
    const hasOnAllSearchResultsClickListener = Object.prototype.hasOwnProperty.call(listeners, "on-all-search-results");
    const searchInput = ref();
    const refs = reactive({
      suggestions: {}
    });
    const searchTermValidationErrorsBag = ref([]);
    const showSuggester = computed(() => {
      return isFocused.value || isClickedIn.value;
    });
    const groupedResults = computed(() => {
      var _a;
      if (props.groupByAssetClass && props.assetClasses.length > 0) {
        return props.assetClasses.map((assetClass) => {
          var _a2;
          return {
            assetClass,
            items: ((_a2 = storeData.value) != null ? _a2 : []).filter((item) => item.assetClass === assetClass)
          };
        }).filter((group) => group.items.length > 0);
      }
      return [{ assetClass: null, items: (_a = storeData.value) != null ? _a : [] }];
    });
    const displayResultsBox = computed(() => {
      return searchTerm.value.length > 0 && showSuggester.value;
    });
    const hasValidSearchTerm = computed(() => {
      return searchTermValidationErrorsBag.value.length === 0;
    });
    const highlight = (text, keyword) => {
      if (keyword) {
        const pattern = new RegExp(`(${keyword})`, "gi");
        return text.replace(pattern, `<mark>${keyword.toUpperCase()}</mark>`);
      }
      return text;
    };
    const isItemSelected = (groupIndex, index) => {
      return selectedGroupIndex.value === groupIndex && selectedItemIndex.value === index;
    };
    const getAssetClass = (item) => {
      var _a;
      return (_a = item.assetClass) != null ? _a : void 0;
    };
    const onClickAway = () => {
      isClickedIn.value = false;
    };
    const onFormFocus = () => {
      isFocused.value = true;
      isClickedIn.value = true;
    };
    const onFormLooseFocus = () => {
      isFocused.value = false;
    };
    const onInput = ($event) => {
      $event.target.composing = false;
    };
    const getItemIdentifier = (groupIndex, index) => {
      return `fds-suggester-item-${groupIndex}-${index}-${elementId}`;
    };
    const getActiveDescendant = () => {
      if (selectedGroupIndex.value === null || selectedItemIndex.value === null) {
        return void 0;
      }
      return `fds-suggester-item-${selectedGroupIndex.value}-${selectedItemIndex.value}-${elementId}`;
    };
    const onKeyPressUp = () => {
      onFormFocus();
      if (selectedGroupIndex.value === null || selectedItemIndex.value === null) {
        selectedGroupIndex.value = groupedResults.value.length - 1;
        selectedItemIndex.value = groupedResults.value[selectedGroupIndex.value].items.length - 1;
        focusOnActiveDescendant();
        return;
      }
      if (selectedItemIndex.value - 1 < 0) {
        selectedGroupIndex.value = selectedGroupIndex.value - 1 >= 0 ? selectedGroupIndex.value - 1 : groupedResults.value.length - 1;
        selectedItemIndex.value = groupedResults.value[selectedGroupIndex.value].items.length - 1;
        focusOnActiveDescendant();
        return;
      }
      selectedItemIndex.value--;
      focusOnActiveDescendant();
    };
    const onKeyPressDown = () => {
      onFormFocus();
      if (selectedGroupIndex.value === null || selectedItemIndex.value === null) {
        selectedGroupIndex.value = 0;
        selectedItemIndex.value = 0;
        focusOnActiveDescendant();
        return;
      }
      if (selectedItemIndex.value + 1 > groupedResults.value[selectedGroupIndex.value].items.length - 1) {
        selectedGroupIndex.value = selectedGroupIndex.value + 1 <= groupedResults.value.length - 1 ? selectedGroupIndex.value + 1 : 0;
        selectedItemIndex.value = 0;
        focusOnActiveDescendant();
        return;
      }
      selectedItemIndex.value++;
      focusOnActiveDescendant();
    };
    const populateSuggestionRef = (groupIndex, index, element) => {
      const identifier = getItemIdentifier(groupIndex, index);
      set(refs.suggestions, identifier, element);
    };
    const focusOnActiveDescendant = () => {
      var _a;
      const activedescendant = getActiveDescendant();
      if (activedescendant && refs.suggestions[activedescendant]) {
        (_a = refs.suggestions[activedescendant]) == null ? void 0 : _a.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start"
        });
      }
    };
    const onKeyPressEnter = () => {
      if (groupedResults.value.length === 1 && groupedResults.value[0].items.length === 1) {
        selectedGroupIndex.value = 0;
        selectedItemIndex.value = 0;
        onSelectSearchResult(groupedResults.value[selectedGroupIndex.value].items[selectedItemIndex.value]);
        return;
      }
      if (selectedGroupIndex.value === null || selectedItemIndex.value === null) {
        emits("on-search", searchTerm.value);
        return;
      }
      onSelectSearchResult(groupedResults.value[selectedGroupIndex.value].items[selectedItemIndex.value]);
      selectedGroupIndex.value = null;
      selectedItemIndex.value = null;
    };
    const onKeyPressEscape = () => {
      var _a;
      if (selectedItemIndex.value === null) {
        (_a = searchInput.value) == null ? void 0 : _a.blur();
        isFocused.value = false;
        isClickedIn.value = false;
      }
      selectedItemIndex.value = null;
    };
    const onAllSearchResultsClick = () => {
      emits("on-all-search-results", searchTerm.value);
      clearSearch();
    };
    const clearSearch = () => {
      searchTerm.value = "";
    };
    const validateSearchTerm = () => {
      var _a, _b;
      searchTermValidationErrorsBag.value = getValidationErrors(searchTerm.value, (_b = (_a = storeMeta.value) == null ? void 0 : _a.validationRules) == null ? void 0 : _b.searchTerm);
    };
    const onSelectSearchResult = (item) => {
      emits("on-select-result", item);
      if (props.closeOnSelect) {
        isFocused.value = false;
        isClickedIn.value = false;
      }
      clearSearch();
    };
    const localizationSupport = useLocalizationSupport(props, {
      translator: inject("translator"),
      numberFormatter: inject("numberFormatter"),
      dateFormatter: inject("dateFormatter")
    });
    const { translate, textFormat } = localizationSupport;
    const storeModule = useStoreModule(
      { storeModule: inject("storeModule") },
      componentInputs,
      emits,
      { requestDataOnComponentInputsChange: false }
    );
    const { storeData, storeMeta, storeCleanUp, storeRequestData, isLoadingStoreData } = storeModule;
    watch(
      componentInputs,
      debounce((_, oldComponentInputs) => {
        validateSearchTerm();
        if (hasValidSearchTerm.value) {
          void storeCleanUp(oldComponentInputs).then(storeRequestData);
        }
      }, 500),
      { immediate: true }
    );
    expose({
      onFormFocus,
      onFormLooseFocus,
      onKeyPressUp,
      onKeyPressDown,
      onKeyPressEnter,
      onKeyPressEscape
    });
    return { __sfc: true, props, emits, listeners, componentInputs, searchTerm, selectedGroupIndex, selectedItemIndex, elementId, isFocused, isClickedIn, hasOnAllSearchResultsClickListener, searchInput, refs, searchTermValidationErrorsBag, showSuggester, groupedResults, displayResultsBox, hasValidSearchTerm, highlight, isItemSelected, getAssetClass, onClickAway, onFormFocus, onFormLooseFocus, onInput, getItemIdentifier, getActiveDescendant, onKeyPressUp, onKeyPressDown, populateSuggestionRef, focusOnActiveDescendant, onKeyPressEnter, onKeyPressEscape, onAllSearchResultsClick, clearSearch, validateSearchTerm, onSelectSearchResult, localizationSupport, translate, textFormat, storeModule, storeData, storeMeta, storeCleanUp, storeRequestData, isLoadingStoreData, UiAssetClassBadge, HoistComponent, showNoResults, showResults, vClickOutside };
  }
});

export { _sfc_main as default };
